import React, { useState, useRef, useEffect, useContext } from "react";

import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import { SocketContext } from "../../context/Socket/SocketContext";

import useSound from "use-sound";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Slider from "@material-ui/core/Slider";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import NotificationsNoneIcon from "@material-ui/icons/NotificationsNone";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import VolumeDownIcon from "@material-ui/icons/VolumeDown";

import TicketListItem from "../TicketListItemCustom";
import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 350,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: "100%",
		maxWidth: 360,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 280,
		},
	},
	iconButton: {
		color: theme.palette.sidebarIcon,
		padding: 10,
		borderRadius: 12,
		transition: "all 0.15s ease",
		"&:hover": {
			backgroundColor: theme.palette.sidebarHover,
		},
	},
	tabs: {
		minHeight: 36,
		borderBottom: `1px solid ${theme.palette.divider}`,
		"& .MuiTab-root": {
			minHeight: 36,
			textTransform: "none",
			fontSize: 12,
			fontWeight: 500,
		},
	},
	volumeContainer: {
		padding: "8px 16px",
		borderTop: `1px solid ${theme.palette.divider}`,
	},
}));

const NotificationsPopOver = ({ volume, setVolume }) => {
	const SOUND_DEBOUNCE_MS = 1200;
	const classes = useStyles();

	const history = useHistory();
	const { user } = useContext(AuthContext);
	const activeTicketRef = useRef(null);
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);
	const [popoverTab, setPopoverTab] = useState(0);

	const [showPendingTickets, setShowPendingTickets] = useState(false);

	const [, setDesktopNotifications] = useState([]);

	const [refreshTime, setRefreshTime] = useState(Date.now());
	const { tickets } = useTickets({ withUnreadMessages: "true", refreshKey: refreshTime });

	const [play] = useSound(alertSound, { volume: volume || 1 });
	const soundAlertRef = useRef();
	const lastSoundAtRef = useRef(0);

	const historyRef = useRef(history);
	const outOfHoursTicketsRef = useRef(new Set());
	const unreadCountRef = useRef(0);
	const originalTitleRef = useRef(typeof document !== "undefined" ? document.title : "PROSIPEChat");

	const socketManager = useContext(SocketContext);

	useEffect(() => {
		const updateActiveTicket = () => {
			const parts = history.location.pathname.split("/");
			if (parts[1] === "tickets" && parts[2]) {
				activeTicketRef.current = parts[2];
			} else {
				activeTicketRef.current = null;
			}
		};
		updateActiveTicket();
		const unlisten = history.listen(updateActiveTicket);
		return () => {
			if (typeof unlisten === "function") unlisten();
		};
	}, [history]);

	useEffect(() => {
		const resetTitle = () => {
			unreadCountRef.current = 0;
			if (typeof document !== "undefined") {
				document.title = originalTitleRef.current || "PROSIPEChat";
			}
		};

		window.addEventListener("focus", resetTitle);
		window.addEventListener("click", resetTitle);
		return () => {
			window.removeEventListener("focus", resetTitle);
			window.removeEventListener("click", resetTitle);
		};
	}, []);

	useEffect(() => {
		let lastRefresh = Date.now();
		const handleReSync = () => {
			const now = Date.now();
			if (now - lastRefresh < 2000) return;
			if (typeof document !== "undefined" && document.visibilityState === "visible") {
				lastRefresh = now;
				setRefreshTime(now);
			}
		};

		window.addEventListener("focus", handleReSync);
		document.addEventListener("visibilitychange", handleReSync);
		return () => {
			window.removeEventListener("focus", handleReSync);
			document.removeEventListener("visibilitychange", handleReSync);
		};
	}, []);

	const playSoundAlert = () => {
		const now = Date.now();
		if (now - lastSoundAtRef.current >= SOUND_DEBOUNCE_MS) {
			lastSoundAtRef.current = now;
			try {
				if (typeof soundAlertRef.current === "function") {
					soundAlertRef.current();
				}
			} catch (e) {
				try {
					const audio = new Audio(alertSound);
					audio.volume = volume !== undefined ? volume : 1;
					audio.play().catch(() => {});
				} catch (err) {}
			}
		}
	};

	useEffect(() => {
		const fetchSettings = async () => {
			try {
				if (user.allTicket === "enabled") {
					setShowPendingTickets(true);
				}
			} catch (err) {
				toastError(err);
			}
		};
		fetchSettings();
	}, [user.allTicket]);

	useEffect(() => {
		soundAlertRef.current = play;

		if (typeof window !== "undefined" && "Notification" in window) {
			if (Notification.permission === "default") {
				Notification.requestPermission().catch(() => {});
			}
		}
	}, [play]);

	useEffect(() => {
		const ticketList = Array.isArray(tickets) ? tickets : [];
		// Não incluir tickets pendentes sem fila selecionada e sem atendente
		const validTickets = ticketList.filter(t => !(t.status === "pending" && !t.queueId && !t.userId));
		if (showPendingTickets) {
			setNotifications(validTickets);
		} else {
			setNotifications(validTickets.filter(ticket => ticket.status !== "pending"));
		}
	}, [tickets, showPendingTickets]);

	useEffect(() => {
		const socket = socketManager.getSocket(user.companyId);

		const onReady = () => {
			socket.emit("joinNotification");
			setRefreshTime(Date.now());
		};
		onReady();

		const onTicketEvent = data => {
			if (data.action === "updateUnread" || data.action === "delete") {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticketId);
					if (ticketIndex !== -1) {
						prevState.splice(ticketIndex, 1);
						return [...prevState];
					}
					return prevState;
				});

				setDesktopNotifications(prevState => {
					const idx = prevState.findIndex(n => n.tag === String(data.ticketId));
					if (idx !== -1) {
						prevState[idx].close();
						prevState.splice(idx, 1);
						return [...prevState];
					}
					return prevState;
				});
			}
		};

		const onAppMessageEvent = data => {
			// ── Suprimir notificações de mensagens fora do expediente ──
			if (data.action === "outOfHours") {
				outOfHoursTicketsRef.current.add(data.ticketId);
				return;
			}

			if (!data.ticket || !data.message) return;

			// Se a conversa já está em atendimento com outro atendente, APENAS esse atendente deve ser notificado
			if (data.ticket.userId && Number(data.ticket.userId) !== Number(user?.id)) {
				return;
			}

			// ── Verificação de permissão para tickets sem atendente ──
			const isAssignedToMe = Number(data.ticket.userId) === Number(user?.id);
			const isUnassigned = !data.ticket.userId;
			const isInMyQueue = user?.queues?.some(q => q.id === data.ticket.queueId);
			const isInMySubQueue = user?.queueOptions?.some(qo => qo.id === data.ticket.queueOptionId);
			const hasNoQueue = !data.ticket.queueId;

			// Não notificar conversas que ainda não selecionaram uma fila e não estão atribuídas a ninguém
			if (hasNoQueue && isUnassigned) {
				return;
			}

			// Determinar se o usuário tem acesso a este ticket
			const hasAccess = user?.allTicket === "enabled" || isAssignedToMe || (
				isUnassigned && (isInMyQueue || isInMySubQueue)
			);

			if (!hasAccess) return;

			if (
				data.action === "create" &&
				!data.message.fromMe &&
				(!data.message.read || data.ticket.status === "pending") &&
				!outOfHoursTicketsRef.current.has(data.ticket.id)
			) {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);
					if (ticketIndex !== -1) {
						prevState[ticketIndex] = data.ticket;
						return [...prevState];
					}
					return [data.ticket, ...prevState];
				});

				// Som toca sempre para mensagem recebida de contato não-grupo
				if (!data.ticket.isGroup) {
					playSoundAlert();
				}

				// Atualizar título da aba com contador de mensagens se não estiver focado
				if (typeof document !== "undefined" && (!document.hasFocus() || document.visibilityState !== "visible")) {
					unreadCountRef.current += 1;
					document.title = `(${unreadCountRef.current}) Nova mensagem - PROSIPEChat`;
				}

				// Checar se o ticket é o que está aberto no momento
				const activeParam = activeTicketRef.current;
				const isCurrentTicket =
					activeParam &&
					(String(data.ticket.uuid) === String(activeParam) ||
						String(data.ticket.id) === String(activeParam) ||
						String(data.message.ticketId) === String(activeParam));

				// Suprimir apenas a janela flutuante desktop caso o atendente esteja ativamente com a aba visível e focada no próprio chat
				const shouldSuppressDesktopNotification =
					isCurrentTicket &&
					document.visibilityState === "visible" &&
					document.hasFocus();

				if (shouldSuppressDesktopNotification || data.ticket.isGroup) return;

				handleNotifications(data);
			}
		};

		const onTicketTransferEvent = data => {
			if (data?.action !== "transfer" || !data.ticket) return;

			// Nunca notificar transferência se o ticket estiver encerrado/fechado
			if (data.ticket.status === "closed") return;

			// Não notificar o próprio atendente que realizou a ação
			if (data.actorUserId && Number(data.actorUserId) === Number(user?.id)) {
				return;
			}

			// Notificar apenas se a transferência foi direcionada a este atendente (ou fila do atendente se sem usuário)
			if (data.targetUserId && Number(data.targetUserId) !== Number(user?.id)) {
				return;
			}
			if (!data.targetUserId && data.targetQueueId) {
				const isInMyQueue = user?.queues?.some(q => q.id === data.targetQueueId);
				if (!isInMyQueue && user?.allTicket !== "enabled") return;
			}

			setNotifications(prevState => {
				const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);
				if (ticketIndex !== -1) {
					const next = [...prevState];
					next[ticketIndex] = data.ticket;
					return next;
				}
				return [data.ticket, ...prevState];
			});

			const title = data.title || "Novo atendimento transferido";
			const body = data.body || "Um atendimento foi transferido para você.";
			toast.info(body);

			if ("Notification" in window && Notification.permission === "granted") {
				try {
					const notification = new Notification(title, {
						body,
						icon: data.ticket?.contact?.profilePicUrl,
						tag: String(data.ticket.id),
						renotify: true,
						requireInteraction: true,
					});
					notification.onclick = event => {
						event.preventDefault();
						window.focus();
						historyRef.current.push(`/tickets/${data.ticket.uuid}`);
						notification.close();
					};
					setDesktopNotifications(prevState => {
						const previous = prevState.find(n => n.tag === notification.tag);
						if (previous) previous.close();
						return [
							notification,
							...prevState.filter(n => n.tag !== notification.tag)
						];
					});
				} catch (error) {
					console.error("Failed to push transfer browser notification", error);
				}
			}

			playSoundAlert();
		};

		socket.on("ready", onReady);
		socket.on(`company-${user.companyId}-ticket`, onTicketEvent);
		socket.on(`company-${user.companyId}-appMessage`, onAppMessageEvent);
		socket.on(`company-${user.companyId}-ticketTransfer`, onTicketTransferEvent);

		return () => {
			socket.off("ready", onReady);
			socket.off(`company-${user.companyId}-ticket`, onTicketEvent);
			socket.off(`company-${user.companyId}-appMessage`, onAppMessageEvent);
			socket.off(`company-${user.companyId}-ticketTransfer`, onTicketTransferEvent);
		};
	}, [user, showPendingTickets, socketManager]);

	const handleNotifications = data => {
		const { message, contact, ticket } = data;

		const options = {
			body: `${message.body} - ${format(new Date(), "HH:mm")}`,
			icon: contact.profilePicUrl,
			tag: ticket.id,
			renotify: true,
		};
		if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
			try {
				const notification = new Notification(
					`${i18n.t("tickets.notification.message")} ${contact.name}`,
					options
				);

				notification.onclick = e => {
					e.preventDefault();
					window.focus();
					historyRef.current.push(`/tickets/${ticket.uuid}`);
				};

				setDesktopNotifications(prevState => {
					const idx = prevState.findIndex(n => n.tag === notification.tag);
					if (idx !== -1) {
						prevState[idx] = notification;
						return [...prevState];
					}
					return [notification, ...prevState];
				});
			} catch (e) {
				console.error("Failed to push browser notification");
			}
		}

		playSoundAlert();
	};

	const handleClick = () => {
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const handleVolumeChange = (_, value) => {
		if (setVolume) setVolume(value);
		localStorage.setItem("volume", value);
	};

	const NotificationTicket = ({ children }) => {
		return <div onClick={handleClickAway}>{children}</div>;
	};

	const notificationList = Array.isArray(notifications) ? notifications : [];
	const totalBadge = notificationList.length;

	return (
		<>
			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Notificações"
				className={classes.iconButton}
			>
				<Badge overlap="rectangular" badgeContent={totalBadge} color="secondary">
					<NotificationsNoneIcon />
				</Badge>
			</IconButton>
			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
				<List dense className={classes.tabContainer}>
					{notificationList.length === 0 ? (
						<ListItem>
							<ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
						</ListItem>
					) : (
						notificationList.map(ticket => (
							<NotificationTicket key={ticket.id}>
								<TicketListItem ticket={ticket} />
							</NotificationTicket>
						))
					)}
				</List>
				<div className={classes.volumeContainer}>
					<Grid container spacing={1} alignItems="center">
						<Grid item>
							<VolumeDownIcon style={{ fontSize: 18 }} />
						</Grid>
						<Grid item xs>
							<Slider
								value={Number(volume) || 1}
								step={0.1}
								min={0}
								max={1}
								onChange={handleVolumeChange}
								size="small"
							/>
						</Grid>
						<Grid item>
							<VolumeUpIcon style={{ fontSize: 18 }} />
						</Grid>
					</Grid>
				</div>
			</Popover>
		</>
	);
};

export default NotificationsPopOver;
